import FieldNames from "../defaults/FieldNames.json";
import CarryoverService from "../services/CarryoverService";
import GroupingService from "../services/GroupingService";
import { formatDateForDisplay } from "./DateHelper";
import { getConditionClassBasedOnDepartureIndex, isFeaturePlannedOrActual, isFeatureTreatment } from "./FeatureHelper";
import { getNoQuotes } from "./FormatHelper";
import { SortFundingRowsForExport } from "./SortingHelper";
import axios from "axios";
import { setShowGlobalLoader } from "@store/slices/globalContextSlice";
import store from "../redux/store";

const featureFieldsValues = FieldNames.featureFields;

const getCostCode = (costCode, costCenter, functionalArea, wbsID) => {
  let finalValue = costCode;
  if(!costCode){
    const finalCostCenter = costCenter ? costCenter : "XXXXXXXXXX";
    const finalFunctionalArea = functionalArea ? functionalArea : "XXXXXXXXX.XXXXXX";
    const finalWbsID = wbsID ? wbsID : "XXXXXXXXXXXX";
    finalValue = `${finalCostCenter}/${finalFunctionalArea}/${finalWbsID}`;
  } 
  return finalValue;
}

const commonExportFirstRow = [FieldNames.featureFields.objectID, FieldNames.featureFields.name,
  "EstimatedTreatmentID", "EstimatedActivityID",
  "ActualTreatmentID", "ActualActivityID",
  FieldNames.featureFields.isPoint, FieldNames.featureFields.parentID,
  FieldNames.featureFields.isParentTreatment,
  FieldNames.featureFields.localID, FieldNames.featureFields.type, FieldNames.featureFields.subType,
  FieldNames.featureFields.category, FieldNames.featureFields.class,
  `Ownership${FieldNames.featureFields.unit}`,
  `Ownership${FieldNames.featureFields.region}`, `Ownership${FieldNames.featureFields.agency}`, 
  `Ownership${FieldNames.featureFields.department}`, FieldNames.featureFields.congressionalDistrictNumber, 
  FieldNames.featureFields.tribeName, FieldNames.featureFields.county, 
  FieldNames.featureFields.state, FieldNames.featureFields.latitude, 
  FieldNames.featureFields.longitude, FieldNames.featureFields.calculatedArea,
  FieldNames.customFieldNamesForExport.durability, FieldNames.featureFields.priority, 
  FieldNames.featureFields.feasibility, FieldNames.featureFields.isWUI, FieldNames.featureFields.isSagebrush,
  FieldNames.featureFields.isFunded, FieldNames.featureFields.isApproved, 
  FieldNames.featureFields.estimatedSuccessProbability, FieldNames.featureFields.initiationDate, 
  FieldNames.featureFields.initiationFiscalYear, FieldNames.featureFields.initiationFiscalQuarter, 
  FieldNames.featureFields.completionDate, FieldNames.featureFields.completionFiscalYear, 
  FieldNames.featureFields.completionFiscalQuarter, FieldNames.featureFields.costCenter,
  FieldNames.featureFields.functionalArea, "WBS", 
  FieldNames.featureFields.costCode,
  FieldNames.featureFields.fundingSource, FieldNames.featureFields.fundingSourceProgram,
  FieldNames.featureFields.fundingSourceCategory, FieldNames.featureFields.fundingSourceSubCategory,
  FieldNames.featureFields.fundingDepartment,
  FieldNames.featureFields.fundingAgency, FieldNames.featureFields.fundingRegion,
  FieldNames.featureFields.fundingUnit, FieldNames.featureFields.fundingSubUnit, 
  FieldNames.featureFields.fundingTribe, FieldNames.featureFields.fundingUnitID,
  FieldNames.featureFields.estimatedPersonnelCost, 
  FieldNames.featureFields.estimatedAssetCost, FieldNames.featureFields.estimatedContractualCost, 
  FieldNames.featureFields.estimatedGrantsFixedCost, FieldNames.featureFields.estimatedOtherCost, 
  FieldNames.featureFields.estimatedTotalCost, FieldNames.featureFields.notes, 
  `Funding Notes`,
  FieldNames.featureFields.localApprovalDate, FieldNames.featureFields.regionalApprovalDate,
  FieldNames.featureFields.agencyApprovalDate, FieldNames.featureFields.departmentApprovalDate, 
  FieldNames.featureFields.fundedDate, FieldNames.featureFields.status, 
  FieldNames.featureFields.statusReason, FieldNames.featureFields.isArchived, 
  FieldNames.featureFields.lastModifiedDate, FieldNames.featureFields.modifiedBy, 
  FieldNames.featureFields.createdOnDate, FieldNames.featureFields.createdBy,
  FieldNames.featureFields.cancelledDate, FieldNames.featureFields.bilFunding,
  "Vegetation Departure Percentage", "Vegetation Departure Index", 
  FieldNames.featureFields.treatmentDriver, FieldNames.featureFields.fundingUnitType,
  FieldNames.featureFields.isRTRL, FieldNames.featureFields.isCarryover,
  "CarryoverReason", "CarryoverNotes",
  FieldNames.featureFields.originalInitiationDate, FieldNames.featureFields.originalInitiationFiscalYear, FieldNames.featureFields.originalInitiationFiscalQuarter,
  "GroupName(s)"
];

export const createCommonExportDataRows = async (featureRows) =>
{
  try
  {
    let polygonIds = featureRows.filter(f => f[FieldNames.featureFields.joinFeature] === FieldNames.joinFeatureValues.polygon)
                            .map(f => f[FieldNames.featureFields.joinID]);
    let lineIds = featureRows.filter(f => f[FieldNames.featureFields.joinFeature] === FieldNames.joinFeatureValues.line)
                            .map(f => f[FieldNames.featureFields.joinID]);
    let pointIds = featureRows.filter(f => f[FieldNames.featureFields.joinFeature] === FieldNames.joinFeatureValues.point)
                            .map(f => f[FieldNames.featureFields.joinID]);

    let cPolyResults = polygonIds.length > 0 ? queryAllCarryover(polygonIds, FieldNames.joinFeatureValues.polygon) : [];
    let cLineResults = lineIds.length > 0 ? queryAllCarryover(lineIds, FieldNames.joinFeatureValues.line) : [];
    let cPointResults = pointIds.length > 0 ? queryAllCarryover(pointIds, FieldNames.joinFeatureValues.point) : [];

    let gPolyResults = polygonIds.length > 0 ? queryAllGroups(polygonIds, FieldNames.joinFeatureValues.polygon) : [];
    let gLineResults = lineIds.length > 0 ? queryAllGroups(lineIds, FieldNames.joinFeatureValues.line) : [];
    let gPointResults = pointIds.length > 0 ? queryAllGroups(pointIds, FieldNames.joinFeatureValues.point) : [];

    let values = await Promise.all([cPolyResults, cLineResults, cPointResults, gPolyResults, gLineResults, gPointResults]);

    let cResults = values[0].concat(values[1]).concat(values[2]);
    let gResults = {
      Groups: (values[3]?.Groups ?? []).concat(values[4]?.Groups ?? []).concat(values[5]?.Groups ?? []),
      GroupItems: (values[3]?.GroupItems ?? []).concat(values[4]?.GroupItems ?? []).concat(values[5]?.GroupItems ?? [])
    }
    let dataRows = [];
    const finalFlattenedDateToShow = featureRows.flat();
    if(finalFlattenedDateToShow?.length > 0){
      finalFlattenedDateToShow.forEach(featureAttr => {
        const isFeatureTreatmentVal = isFeatureTreatment(false, featureAttr, featureFieldsValues);
        const isFeatureActuals = isFeaturePlannedOrActual(false, featureAttr, featureFieldsValues);
        
        const featureCarryoverEntries = cResults?.filter(x => x[FieldNames.carryoverFields.joinID] === featureAttr[FieldNames.featureFields.id]);
        const featureCarryoverReason = featureCarryoverEntries?.map(x => x[FieldNames.carryoverFields.reason]).join("|");
        const featureCarryoverNotes = featureCarryoverEntries?.map(x => x[FieldNames.carryoverFields.notes] ? x[FieldNames.carryoverFields.notes] : "null").join("|");
        const featureGroupIds = gResults?.GroupItems ? gResults.GroupItems.filter(x => x[FieldNames.treatmentActivityGroupItems.joinID] === featureAttr[FieldNames.featureFields.id]).map(x => x[FieldNames.treatmentActivityGroupItems.treatmentActivityGroupJoinID]) : [];
        const featureGroupDistinctIds = [...new Set(featureGroupIds)];
        const featureGroupNameConcat = gResults?.Groups ? gResults.Groups.filter(x => featureGroupDistinctIds.includes(x[FieldNames.treatmentActivityGroups.id])).map(x => x[FieldNames.treatmentActivityGroups.treatmentActivityGroupName]).join("|") : "";
  
        let fields;
        let vegDeparturePercentage;
        let vegDepartureIndex;
        if(isFeatureTreatmentVal) {
          if(isFeatureActuals){
            fields = FieldNames.featureFields;
            vegDeparturePercentage = null;
            vegDepartureIndex = getConditionClassBasedOnDepartureIndex(featureAttr[fields.vegDeparturePercentageManual]);
          } else {
            fields = FieldNames.featureFields;
            vegDeparturePercentage = featureAttr[fields.vegDeparturePercentageManual];
            vegDepartureIndex = getConditionClassBasedOnDepartureIndex(featureAttr[fields.vegDeparturePercentageManual]);
          }
        } else {
          if(isFeatureActuals){
            fields = FieldNames.featureFields;
            vegDeparturePercentage = null;
            vegDepartureIndex = featureAttr[fields.vegDeparturePercentageManual];
          } else {
            fields = FieldNames.featureFields;
            vegDeparturePercentage = featureAttr[fields.vegDeparturePercentageManual];
            vegDepartureIndex = null;
          }
        }

        const trtID = featureAttr[fields.class] === FieldNames.classTypes.treatment ? featureAttr[fields.id] : "";
        const actID = featureAttr[fields.class] === FieldNames.classTypes.activity ? featureAttr[fields.id] : "";
        const actualTrtID = featureAttr[fields.class] === FieldNames.classTypes.actualTreatment ? featureAttr[fields.id] : "";
        const actualActID = featureAttr[fields.class] === FieldNames.classTypes.actualActivity ? featureAttr[fields.id] : "";
  
        let newRow = [
          featureAttr[FieldNames.featureFields.objectID], featureAttr[fields.name],
          trtID, actID,
          actualTrtID, actualActID,
          featureAttr[fields.isPoint], featureAttr[fields.parentID],
          featureAttr[fields.isParentTreatment],
          featureAttr[fields.localID], featureAttr[fields.type],
          featureAttr[fields.subType],
          featureAttr[fields.category], featureAttr[fields.class],
          featureAttr[fields.unit], featureAttr[fields.region], featureAttr[fields.agency], 
          featureAttr[fields.department], featureAttr[fields.congressionalDistrictNumber], 
          featureAttr[fields.tribeName], featureAttr[fields.county], 
          featureAttr[fields.state], featureAttr[fields.latitude], 
          featureAttr[fields.longitude], featureAttr[fields.calculatedArea],
          featureAttr[fields.durability], featureAttr[fields.priority], 
          featureAttr[fields.feasibility], featureAttr[fields.isWUI],
          ((featureAttr[fields.isSagebrush] && featureAttr[fields.isSagebrush] === FieldNames.yesNo.yes) ? FieldNames.yesNo.yes : FieldNames.yesNo.no),
          featureAttr[fields.isFunded], featureAttr[fields.isApproved], 
          getNoQuotes(featureAttr[fields.estimatedSuccessProbability]), formatDateForDisplay(featureAttr[fields.initiationDate]),
          featureAttr[fields.initiationFiscalYear], featureAttr[fields.initiationFiscalQuarter], 
          formatDateForDisplay(featureAttr[fields.completionDate]), featureAttr[fields.completionFiscalYear],
          featureAttr[fields.completionFiscalQuarter], 
          featureAttr[FieldNames.featureFields.costCenter] ? featureAttr[FieldNames.featureFields.costCenter] : "XXXXXXXXXX",
          featureAttr[FieldNames.featureFields.functionalArea] ? featureAttr[FieldNames.featureFields.functionalArea] : "XXXXXXXXX.XXXXXX", 
          featureAttr[FieldNames.featureFields.wbsID] ? featureAttr[FieldNames.featureFields.wbsID] : "XXXXXXXXXXXX", 
          getCostCode(featureAttr[FieldNames.featureFields.costCode], featureAttr[FieldNames.featureFields.costCenter], featureAttr[FieldNames.featureFields.functionalArea], featureAttr[FieldNames.featureFields.wbsID]),
          featureAttr[FieldNames.featureFields.fundingSource], featureAttr[FieldNames.featureFields.fundingSourceProgram],
          featureAttr[FieldNames.featureFields.fundingSourceCategory], featureAttr[FieldNames.featureFields.fundingSourceSubCategory],
          featureAttr[FieldNames.featureFields.fundingDepartment],
          featureAttr[FieldNames.featureFields.fundingAgency], featureAttr[FieldNames.featureFields.fundingRegion],
          featureAttr[FieldNames.featureFields.fundingUnit], featureAttr[FieldNames.featureFields.fundingSubUnit],
          featureAttr[fields.fundingTribe], featureAttr[fields.fundingUnitID],
          featureAttr[FieldNames.fundingFields.personnelCost], 
          featureAttr[FieldNames.fundingFields.assetCost], featureAttr[FieldNames.fundingFields.contractualCost], 
          featureAttr[FieldNames.fundingFields.grantsFixedCost], featureAttr[FieldNames.fundingFields.otherCost], 
          featureAttr[FieldNames.fundingFields.totalCost], featureAttr[fields.notes], 
          featureAttr[fields.contributedNotes],
          formatDateForDisplay(featureAttr[fields.localApprovalDate]), formatDateForDisplay(featureAttr[fields.regionalApprovalDate]),
          formatDateForDisplay(featureAttr[fields.agencyApprovalDate]), formatDateForDisplay(featureAttr[fields.departmentApprovalDate]),
          formatDateForDisplay(featureAttr[fields.fundedDate]), featureAttr[fields.status],
          featureAttr[fields.statusReason], featureAttr[fields.isArchived], 
          formatDateForDisplay(featureAttr[fields.lastModifiedDate]), featureAttr[fields.modifiedBy],
          formatDateForDisplay(featureAttr[fields.createdOnDate]), featureAttr[fields.createdBy],
          formatDateForDisplay(featureAttr[fields.cancelledDate]),
          featureAttr[fields.bilFunding],
          vegDeparturePercentage, vegDepartureIndex, 
          featureAttr[fields.treatmentDriver], featureAttr[fields.fundingUnitType],
          ((featureAttr[fields.isRTRL] && featureAttr[fields.isRTRL] === FieldNames.yesNo.yes) ? FieldNames.yesNo.yes : FieldNames.yesNo.no),
          ((featureAttr[fields.isCarryover] && featureAttr[fields.isCarryover] === FieldNames.yesNo.yes) ? FieldNames.yesNo.yes : FieldNames.yesNo.no),
          ((featureAttr[fields.isCarryover] && featureAttr[fields.isCarryover] === FieldNames.yesNo.yes) ? featureCarryoverReason : ""),
          ((featureAttr[fields.isCarryover] && featureAttr[fields.isCarryover] === FieldNames.yesNo.yes) ? featureCarryoverNotes : ""),
          formatDateForDisplay(featureAttr[fields.originalInitiationDate]), featureAttr[fields.originalInitiationFiscalYear], featureAttr[fields.originalInitiationFiscalQuarter],
          featureGroupNameConcat
        ];
        dataRows.push(newRow);
      });
    }
    return dataRows;
  }
  catch(e)
  {
    throw e;
  }
}

const queryAllCarryover = (allIDs, joinFeature) => {
  return new Promise((resolve, reject) => {
    let carryoverCollection = [];

    CarryoverService.getLatestMultipleFeaturesCarryoverEntry(
      joinFeature,
      allIDs,
      "JoinID, JoinFeature, Reason, Notes"
    ).then((cResp) => {
      if (cResp && cResp.length > 0) {
        carryoverCollection = cResp;
      }
      resolve(carryoverCollection);
    }).catch(error => {
      reject(error);
    });
  });
}

const queryAllGroups = (allIDs, joinFeature) => {
  return new Promise((resolve, reject) => {
    let groupCollection = [];

    GroupingService.getAllGroupItemsForFeaturesByIds(
      joinFeature,
      allIDs
      ).then((cResp) => {
      if (cResp && cResp.Groups && cResp.Groups.length > 0 && cResp.GroupItems && cResp.GroupItems.length > 0) {
        groupCollection = cResp;
      }
      resolve(groupCollection);
    }).catch(error => {
      reject(error);
    });
  });
}

export const generateFeatureDataPromise = async (features) => {
  const firstRow = commonExportFirstRow;
  let dataRows = [firstRow]
  let dataToShow = [];
  features.forEach((action, index) => {
    isCurrentTreatment = (action[FieldNames.featureFields.class] === FieldNames.classTypes.treatment || action[FieldNames.featureFields.class] === FieldNames.classTypes.actualTreatment);
    isCurrentActual = (action[FieldNames.featureFields.class] === FieldNames.classTypes.actualTreatment || action[FieldNames.featureFields.class] === FieldNames.classTypes.actualActivity);
    dataToShow.push(action);
  })

  if(dataToShow?.length > 0){
    let sortedRows = SortFundingRowsForExport(dataToShow);
    let exportRows = await createCommonExportDataRows(sortedRows);
    if(!exportRows?.length > 0)
    {
      exportRows = [];
    }
    dataRows = [...dataRows, ...exportRows];
    return dataRows    
  }
  else
  {
    return [[]];
  }
}

export const generateMetadata = (definitionExpression) => {
  let metaDataRows = [];
  let expressionRow = ['Where Clause:', definitionExpression];
  metaDataRows.push(expressionRow);
  return metaDataRows;
}

export const requestSingleDownload = async (nameOrFn, fileExtension, controller, config, retries=0, currentTry=0) => {
  let fileName = (typeof(nameOrFn) === 'string') ? nameOrFn : nameOrFn(currentTry);
  try {
    const response = await axios.get(generateStaticDownloadUrl(fileName, fileExtension, config), {
      signal: controller.signal,
      responseType: 'blob',
      onDownloadProgress: createProgressCallback(),
    });
    attachAndClickLink(response, `${fileName}${fileExtension}`);
    return; // success, resolve
  } catch (e) {
    console.error(e);
    if (currentTry >= retries) {
      throw e;
    }
    return requestSingleDownload(nameOrFn, fileExtension, controller, config, retries, currentTry+1);
  }
}

const attachAndClickLink = (response, finalFileNameAndExtension) => {
    // create file link in browser's memory
    const href = window.URL.createObjectURL(new Blob([response.data]));

    // create "a" HTML element with href to file & click
    const link = document.createElement('a');
    link.href = href;
    link.setAttribute('download', finalFileNameAndExtension);
    document.body.appendChild(link);
    link.click();

    // clean up "a" element & remove ObjectURL
    document.body.removeChild(link);
    URL.revokeObjectURL(href);
}

export const generateStaticDownloadUrl = (fileName, fileExtension, config) => {
  return `${config.fileDomainBasePath[config.env]}/files/${fileName}${fileExtension}`;
}

export const createProgressCallback = () => {
  return function (progressEvent) {
    let percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
    if(percentCompleted === 100) {
      store.dispatch(setShowGlobalLoader(false));
    }
  }
}